const WALLET_DEBUG_STORAGE_KEY = "pm:walletDebug";
const walletDebugQueryKeys = ["pm_wallet_debug", "pm_debug_wallet"];
const lastWalletDebugEntries = new Map();
let lastAppliedDebugSearch = "";

export function isWalletDebugEnabled({ location = readLocation(), storage = readLocalStorage() } = {}) {
  if (isEnabledByQuery(location, storage)) return true;

  try {
    return storage?.getItem(WALLET_DEBUG_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function walletDebug(event, details = {}) {
  if (!isWalletDebugEnabled()) return;

  const sanitizedDetails = sanitizeForWalletDebug(details);
  const dedupeKey = stringifyDebugDetails(sanitizedDetails);
  if (lastWalletDebugEntries.get(event) === dedupeKey) return;
  lastWalletDebugEntries.set(event, dedupeKey);
  if (lastWalletDebugEntries.size > 100) {
    lastWalletDebugEntries.delete(lastWalletDebugEntries.keys().next().value);
  }

  console.info(`[pm-wallet] ${event}`, sanitizedDetails);
}

export function sanitizeForWalletDebug(value) {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForWalletDebug(item));
  }
  if (!value || typeof value !== "object") {
    return sanitizePrimitive(value);
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, sanitizeDebugEntry(key, item)])
  );
}

function sanitizeDebugEntry(key, value) {
  const normalizedKey = String(key || "").toLowerCase();
  if (normalizedKey.includes("token") || normalizedKey.includes("secret") || normalizedKey.includes("authorization")) {
    if (typeof value === "boolean") return value;
    return String(value || "").trim() ? "[present]" : "[missing]";
  }
  if (normalizedKey.includes("wallet") || normalizedKey.includes("address") || normalizedKey === "userId".toLowerCase()) {
    if (Array.isArray(value)) {
      return value.map((item) => sanitizeDebugEntry(key, item));
    }
    if (value && typeof value === "object") {
      return sanitizeForWalletDebug(value);
    }
    if (typeof value !== "string") return sanitizePrimitive(value);
    return maskWalletAddress(value);
  }
  return sanitizeForWalletDebug(value);
}

function sanitizePrimitive(value) {
  if (typeof value === "string" && value.length > 96) {
    return `${value.slice(0, 48)}...[${value.length} chars]`;
  }
  return value;
}

function maskWalletAddress(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= 12) return text;
  return `${text.slice(0, 6)}...${text.slice(-6)}`;
}

function isEnabledByQuery(location, storage) {
  const search = String(location?.search || "");
  if (!search) return false;

  let params;
  try {
    params = new URLSearchParams(search);
  } catch {
    return false;
  }

  for (const key of walletDebugQueryKeys) {
    const value = params.get(key);
    if (value === "1" || value === "true") {
      if (lastAppliedDebugSearch !== search) {
        try {
          storage?.setItem(WALLET_DEBUG_STORAGE_KEY, "1");
        } catch {
          // Debug logging still works for this page load.
        }
        lastAppliedDebugSearch = search;
      }
      return true;
    }
    if (value === "0" || value === "false") {
      if (lastAppliedDebugSearch !== search) {
        try {
          storage?.removeItem(WALLET_DEBUG_STORAGE_KEY);
        } catch {
          // Ignore storage failures.
        }
        lastAppliedDebugSearch = search;
      }
      return false;
    }
  }

  return false;
}

function readLocation() {
  return typeof window === "undefined" ? null : window.location;
}

function readLocalStorage() {
  return typeof window === "undefined" ? null : window.localStorage;
}

function stringifyDebugDetails(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
